import React, { useEffect, useState, useCallback, useRef } from "react";
import { Link } from "react-router-dom";
import {
  ArrowLeft, Music, Search, Play, Pause, Heart, Loader2, Plug, Unplug, ExternalLink,
  Disc3, Sparkles, X, Headphones, Filter, Eye, EyeOff, Film, Plus, Check, Volume2,
} from "lucide-react";
import { toast } from "sonner";

import PageHeader from "@/components/PageHeader";
import { api, API_BASE } from "@/lib/api";
import Hls from "hls.js";
import { appConfirm } from "@/components/AppDialogHost";

function fmtTime(sec) {
  if (!sec || sec < 1) return "—";
  const m = Math.floor(sec / 60), s = Math.floor(sec % 60);
  return `${m}:${s.toString().padStart(2, "0")}`;
}

const GENRES = ["", "Hip-Hop", "R&B", "Pop", "Rock", "Electronic", "Ambient", "Jazz", "Country", "Reggae", "Cinematic"];
const MOODS = ["", "Epic", "Happy", "Calm", "Dark", "Hopeful", "Suspense", "Sad", "Romantic"];
const ENERGIES = ["", "low", "medium", "high"];

function TrackRow({ track, onPlay, isPlaying, isFavorite, onToggleFav, onAttach, onDropImaging, isDropping }) {
  return (
    <div className="flex items-center gap-3 rounded-md border border-line bg-bg-elevated p-2.5 hover:border-brand-amber/40 transition" data-testid={`track-row-${track.track_id}`}>
      <button
        onClick={() => onPlay(track)}
        className="h-10 w-10 rounded-md bg-brand-amber/10 text-brand-amber grid place-items-center shrink-0 hover:bg-brand-amber/20"
        data-testid={`play-${track.track_id}`}
        title={isPlaying ? "Pause" : "Play preview"}
      >
        {isPlaying ? <Pause className="h-4 w-4" /> : <Play className="h-4 w-4" />}
      </button>
      {track.cover_url ? (
        <img src={track.cover_url} alt="" className="h-10 w-10 rounded object-cover shrink-0" />
      ) : (
        <div className="h-10 w-10 rounded bg-bg grid place-items-center shrink-0"><Disc3 className="h-4 w-4 text-ink-muted" /></div>
      )}
      <div className="flex-1 min-w-0">
        <div className="text-sm font-medium text-ink-primary truncate">{track.title || "Untitled"}</div>
        <div className="text-[11px] text-ink-muted truncate">{track.artist || "—"}</div>
      </div>
      <div className="hidden sm:flex items-center gap-3 shrink-0 text-[11px] font-mono text-ink-muted">
        <span title="genre">{(track.genre || "").split(",")[0] || "—"}</span>
        <span title="mood">{(track.mood || "").split(",")[0]}</span>
        <span title="BPM">{track.bpm ? `${track.bpm} BPM` : ""}</span>
        <span>{fmtTime(track.duration_sec)}</span>
      </div>
      <button
        onClick={() => onAttach(track)}
        className="h-8 px-2 rounded-md grid place-items-center shrink-0 text-ink-muted hover:text-brand-blue hover:bg-brand-blue/10 transition text-[11px] font-mono uppercase tracking-wider inline-flex items-center gap-1"
        data-testid={`attach-${track.track_id}`}
        title="Attach as music bed to a Vision project"
      >
        <Film className="h-3.5 w-3.5" /> Attach
      </button>
      <button
        onClick={() => onDropImaging(track)}
        disabled={isDropping}
        className="h-8 px-2 rounded-md grid place-items-center shrink-0 text-ink-muted hover:text-brand-amber hover:bg-brand-amber/10 transition text-[11px] font-mono uppercase tracking-wider inline-flex items-center gap-1 disabled:opacity-50"
        data-testid={`drop-imaging-${track.track_id}`}
        title="Drop into Imaging as a music bed"
      >
        {isDropping ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Plus className="h-3.5 w-3.5" />} Bed
      </button>
      <button
        onClick={() => onToggleFav(track)}
        className={`h-8 w-8 rounded-md grid place-items-center shrink-0 transition ${
          isFavorite ? "text-brand-onair bg-brand-onair/10" : "text-ink-muted hover:text-brand-onair hover:bg-brand-onair/5"
        }`}
        data-testid={`fav-${track.track_id}`}
        title={isFavorite ? "Remove favorite" : "Add to favorites"}
      >
        <Heart className={`h-4 w-4 ${isFavorite ? "fill-current" : ""}`} />
      </button>
    </div>
  );
}

export default function AudioStudio() {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("search"); // search | favorites

  // Connect modal
  const [showConnect, setShowConnect] = useState(false);
  const [credential, setCredential] = useState("");
  const [showCred, setShowCred] = useState(false);
  const [connecting, setConnecting] = useState(false);

  // Search
  const [query, setQuery] = useState("");
  const [genre, setGenre] = useState("");
  const [mood, setMood] = useState("");
  const [energy, setEnergy] = useState("");
  const [bpmMin, setBpmMin] = useState("");
  const [bpmMax, setBpmMax] = useState("");
  const [hasVocals, setHasVocals] = useState("");
  const [searching, setSearching] = useState(false);
  const [results, setResults] = useState({ items: [], total: 0 });

  // Favorites
  const [favorites, setFavorites] = useState({ items: [], total: 0 });
  const [favIds, setFavIds] = useState(new Set());

  // Audio playback
  const audioRef = useRef(null);
  const hlsRef = useRef(null);
  const volumeRef = useRef(0.9);
  const [playingId, setPlayingId] = useState("");
  const [npTrack, setNpTrack] = useState(null); // track in the sticky player bar
  const [curTime, setCurTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [volume, setVolume] = useState(0.9);

  // Attach to Vision project
  const [attachTrack, setAttachTrack] = useState(null); // track being attached
  const [visionProjects, setVisionProjects] = useState([]);
  const [visionLoading, setVisionLoading] = useState(false);
  const [attaching, setAttaching] = useState("");

  // Drop into Imaging
  const [droppingId, setDroppingId] = useState("");
  const dropToImaging = async (track) => {
    if (droppingId) return;
    setDroppingId(track.track_id);
    try {
      const { data } = await api.post("/audio-studio/drop-to-imaging", { track_id: track.track_id });
      toast.success(
        data.already_imported
          ? "Already in your Imaging library"
          : `"${track.title || "Track"}" added to Imaging as a music bed`,
        {
          duration: 8000,
          action: { label: "Open Imaging", onClick: () => window.location.assign("/dashboard/imaging") },
        },
      );
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Import failed", { duration: 8000 });
    } finally { setDroppingId(""); }
  };

  const loadStatus = useCallback(async () => {
    setLoading(true);
    try { const r = await api.get("/audio-studio/status"); setStatus(r.data); }
    catch (e) { setStatus({ error: e?.response?.data?.detail || "Could not load" }); }
    finally { setLoading(false); }
  }, []);

  const loadFavorites = useCallback(async () => {
    try {
      const r = await api.get("/audio-studio/favorites");
      setFavorites(r.data);
      setFavIds(new Set((r.data.items || []).map((i) => i.track_id)));
    } catch { /* gated */ }
  }, []);

  useEffect(() => { loadStatus(); loadFavorites(); }, [loadStatus, loadFavorites]);

  const connect = async () => {
    setConnecting(true);
    try {
      const r = await api.post("/audio-studio/connect", { credential });
      if (r.data.warning) {
        toast.warning(r.data.warning, { duration: 10000 });
      } else {
        toast.success(`Connected${r.data.account?.email ? ` as ${r.data.account.email}` : ""}`);
      }
      setShowConnect(false); setCredential("");
      loadStatus();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Connection failed");
    } finally { setConnecting(false); }
  };

  const disconnect = async () => {
    if (!await appConfirm("Disconnect Epidemic Sound? Your favorites will be kept.")) return;
    try { await api.delete("/audio-studio/connect"); toast.success("Disconnected"); loadStatus(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Disconnect failed"); }
  };

  const [testing, setTesting] = useState(false);
  const testConnection = async () => {
    setTesting(true);
    try {
      const r = await api.post("/audio-studio/test-connection");
      if (r.data.ok) {
        const sample = r.data.sample;
        toast.success(
          sample
            ? `${r.data.message} Sample: "${sample.title}"${sample.artist ? ` — ${sample.artist}` : ""}`
            : r.data.message,
          { duration: 8000 }
        );
      } else {
        const status = r.data.status;
        const msg = r.data.message || "Connection test failed";
        if (status === 401 || status === 403) {
          toast.error(`Credentials rejected by Epidemic Sound (HTTP ${status}). Reconnect with a fresh key.`, { duration: 10000 });
        } else if (status === 0 || status >= 500) {
          toast.warning(`Couldn't reach Epidemic Sound: ${msg}`, { duration: 10000 });
        } else {
          toast.error(`HTTP ${status}: ${msg}`, { duration: 10000 });
        }
      }
      loadStatus();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Test connection failed");
    } finally { setTesting(false); }
  };

  const search = async () => {
    setSearching(true); setResults({ items: [], total: 0 });
    try {
      const body = {
        query, genre, mood, energy,
        bpm_min: bpmMin ? Number(bpmMin) : null,
        bpm_max: bpmMax ? Number(bpmMax) : null,
        has_vocals: hasVocals === "" ? null : hasVocals === "yes",
        limit: 30, offset: 0,
      };
      const r = await api.post("/audio-studio/search", body);
      setResults(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Search failed");
    } finally { setSearching(false); }
  };

  const stopAudio = () => {
    try { hlsRef.current?.destroy(); } catch { /* noop */ }
    hlsRef.current = null;
    audioRef.current?.pause();
    audioRef.current = null;
    setPlayingId("");
  };

  const wireAudio = (a, track) => {
    a.volume = volumeRef.current;
    a.ontimeupdate = () => setCurTime(a.currentTime || 0);
    a.onloadedmetadata = () => setDuration(isFinite(a.duration) ? a.duration : track.duration_sec || 0);
    a.ondurationchange = () => { if (isFinite(a.duration) && a.duration > 0) setDuration(a.duration); };
    a.onended = () => setPlayingId("");
  };

  const startPlayerBar = (track) => {
    setNpTrack(track);
    setCurTime(0);
    setDuration(track.duration_sec || 0);
  };

  const pausePlayback = () => { audioRef.current?.pause(); setPlayingId(""); };

  const resumePlayback = () => {
    if (audioRef.current && npTrack) {
      audioRef.current.play().catch(() => { /* noop */ });
      setPlayingId(npTrack.track_id);
    } else if (npTrack) {
      playPreview(npTrack);
    }
  };

  const closePlayer = () => {
    stopAudio();
    setNpTrack(null);
    setCurTime(0);
    setDuration(0);
  };

  const seekTo = (v) => {
    const t = Number(v);
    if (audioRef.current && isFinite(t)) { audioRef.current.currentTime = t; setCurTime(t); }
  };

  const changeVolume = (v) => {
    const vol = Math.max(0, Math.min(1, Number(v)));
    setVolume(vol);
    volumeRef.current = vol;
    if (audioRef.current) audioRef.current.volume = vol;
  };

  const playMp3Fallback = async (track) => {
    try {
      const { data } = await api.get(`/audio-studio/stream-url/${encodeURIComponent(track.track_id)}?prefer=mp3`);
      const a = new Audio(data.url);
      wireAudio(a, track);
      a.onerror = () => { toast.error("Preview playback failed"); setPlayingId(""); };
      a.play().catch(() => { toast.error("Preview playback failed"); setPlayingId(""); });
      audioRef.current = a;
      setPlayingId(track.track_id);
    } catch {
      toast.error("No preview available for this track");
      setPlayingId("");
    }
  };

  const playPreview = async (track) => {
    if (playingId === track.track_id) { pausePlayback(); return; }
    if (npTrack?.track_id === track.track_id && audioRef.current) {
      // Same track paused in the player bar — just resume
      audioRef.current.play().catch(() => { /* noop */ });
      setPlayingId(track.track_id);
      return;
    }
    stopAudio();
    setPlayingId(track.track_id);
    startPlayerBar(track);
    let url = track.preview_url;
    if (!url) {
      // Epidemic stream URLs are short-lived — resolve a fresh one per play.
      try {
        const { data } = await api.get(`/audio-studio/stream-url/${encodeURIComponent(track.track_id)}`);
        url = data.url;
      } catch (e) {
        toast.error(e?.response?.data?.detail || "No preview available for this track");
        setPlayingId("");
        return;
      }
    }
    const a = new Audio();
    wireAudio(a, track);
    const isHlsUrl = /\.m3u8($|\?)|\/hls\//.test(url);
    if (isHlsUrl && Hls.isSupported()) {
      const hls = new Hls();
      hlsRef.current = hls;
      hls.loadSource(url);
      hls.attachMedia(a);
      hls.on(Hls.Events.ERROR, (_e, d) => {
        if (d?.fatal) {
          // e.g. browser lacks AAC/MSE — retry with the direct MP3
          try { hls.destroy(); } catch { /* noop */ }
          hlsRef.current = null;
          audioRef.current = null;
          playMp3Fallback(track);
        }
      });
      a.play().catch(() => { /* playback starts once HLS media attaches */ });
    } else {
      a.src = url; // direct MP3, or native HLS on Safari
      a.onerror = () => { toast.error("Preview playback failed"); setPlayingId(""); };
      a.play().catch(() => { toast.error("Preview playback failed"); setPlayingId(""); });
    }
    audioRef.current = a;
  };

  const toggleFav = async (track) => {
    try {
      const r = await api.post(`/audio-studio/favorite/${encodeURIComponent(track.track_id)}`);
      setFavIds((prev) => {
        const next = new Set(prev);
        if (r.data.favorited) next.add(track.track_id); else next.delete(track.track_id);
        return next;
      });
      loadFavorites(); loadStatus();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Favorite failed");
    }
  };

  const openAttach = async (track) => {
    setAttachTrack(track);
    setVisionLoading(true);
    try {
      const r = await api.get("/vision/projects");
      setVisionProjects(r.data?.items || []);
    } catch (e) {
      const msg = e?.response?.data?.detail;
      if (e?.response?.status === 402) {
        toast.error("Vision Studio is on Pro Radio + Network plans.");
      } else {
        toast.error(typeof msg === "string" ? msg : "Could not load Vision projects");
      }
      setVisionProjects([]);
    } finally { setVisionLoading(false); }
  };

  const doAttach = async (project) => {
    if (!attachTrack || !project?.project_id) return;
    setAttaching(project.project_id);
    try {
      await api.post("/audio-studio/attach", {
        project_id: project.project_id,
        track_id: attachTrack.track_id,
      });
      toast.success(`Attached to "${project.title}"`);
      setAttachTrack(null);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Attach failed");
    } finally { setAttaching(""); }
  };

  if (loading) return <div className="p-10 text-center font-mono text-xs text-ink-muted">Loading Audio Studio…</div>;

  if (status?.error) {
    return (
      <div className="p-6 md:p-10 max-w-3xl mx-auto" data-testid="audio-studio-locked">
        <Link to="/dashboard" className="inline-flex items-center gap-1 text-xs text-ink-muted hover:text-brand-amber mb-4">
          <ArrowLeft className="h-3.5 w-3.5" /> Back to dashboard
        </Link>
        <div className="rounded-xl border border-brand-amber/30 bg-brand-amber/5 p-8 text-center">
          <Headphones className="h-10 w-10 text-brand-amber mx-auto mb-3" />
          <h2 className="font-display text-xl font-bold mb-2">Audio Studio is on Pro Radio + Network</h2>
          <p className="text-sm text-ink-muted mb-4">{typeof status.error === "string" ? status.error : "Upgrade to unlock Epidemic Sound."}</p>
          <Link to="/dashboard/billing" className="inline-block rounded-md bg-brand-amber text-bg font-bold px-4 py-2 text-sm">View plans →</Link>
        </div>
      </div>
    );
  }

  const connected = !!status?.connected;

  return (
    <div className={`p-6 md:p-10 max-w-7xl mx-auto animate-fade-up ${npTrack ? "pb-28" : ""}`} data-testid="audio-studio-page">
      <Link to="/dashboard" className="inline-flex items-center gap-1 text-xs text-ink-muted hover:text-brand-amber transition mb-4">
        <ArrowLeft className="h-3.5 w-3.5" /> Back to dashboard
      </Link>

      <PageHeader
        eyebrow="// AUDIO STUDIO"
        title="Licensed music + SFX for every project."
        description="Search the Epidemic Sound catalog — 55,000+ tracks, 250,000+ effects — preview, favorite, and drop straight into Imaging, News, Vision and Commercial studios."
      />

      {/* Connection banner */}
      <div className={`mb-6 flex items-center gap-3 rounded-xl border p-4 ${
        connected ? "border-emerald-400/30 bg-emerald-500/5" : "border-brand-onair/40 bg-brand-onair/5"
      }`} data-testid="connection-banner">
        {connected ? <Plug className="h-5 w-5 text-emerald-300 shrink-0" /> : <Unplug className="h-5 w-5 text-brand-onair shrink-0" />}
        <div className="flex-1 min-w-0">
          <div className="font-medium text-sm">
            {connected ? "Connected to Epidemic Sound" : "Not connected"}
            {connected && status?.verified === false && (
              <span className="ml-2 inline-block rounded-full border border-brand-amber/40 bg-brand-amber/10 text-brand-amber px-2 py-0.5 text-[10px] font-mono uppercase tracking-wider" data-testid="unverified-badge">
                Unverified
              </span>
            )}
            {connected && status?.verified === true && (
              <span className="ml-2 inline-block rounded-full border border-emerald-400/40 bg-emerald-500/10 text-emerald-300 px-2 py-0.5 text-[10px] font-mono uppercase tracking-wider" data-testid="verified-badge">
                Verified
              </span>
            )}
          </div>
          <div className="text-xs text-ink-muted mt-0.5 truncate">
            {connected ? (status.account_email || "BYOK credential active") : "Paste your Epidemic Sound Bearer JWT or accessKeyId:accessKeySecret to begin."}
          </div>
        </div>
        {connected ? (
          <div className="flex items-center gap-2">
            <button
              onClick={testConnection}
              disabled={testing}
              data-testid="test-connection-btn"
              className="text-xs px-3 py-1.5 rounded-md border border-brand-amber/40 bg-brand-amber/10 text-brand-amber hover:bg-brand-amber/20 disabled:opacity-50 inline-flex items-center gap-1.5"
              title="Run a live 1-result search against Epidemic Sound to verify your key"
            >
              {testing ? "Testing…" : "Test connection"}
            </button>
            <button onClick={disconnect} data-testid="disconnect-btn" className="text-xs px-3 py-1.5 rounded-md border border-brand-onair/30 text-brand-onair hover:bg-brand-onair/5">
              Disconnect
            </button>
          </div>
        ) : (
          <button onClick={() => setShowConnect(true)} data-testid="open-connect-btn" className="text-xs px-3 py-1.5 rounded-md border border-brand-amber/30 bg-brand-amber/10 text-brand-amber font-bold">
            Connect
          </button>
        )}
      </div>

      {/* Tabs */}
      <div className="mb-4 inline-flex rounded-md border border-line bg-bg-surface p-1" data-testid="audio-tabs">
        <button onClick={() => setTab("search")} data-testid="tab-search"
          className={`text-xs px-3 py-1.5 rounded transition ${tab === "search" ? "bg-brand-amber text-bg font-bold" : "text-ink-muted hover:text-ink-primary"}`}>
          <Search className="h-3.5 w-3.5 inline mr-1" /> Search
        </button>
        <button onClick={() => setTab("favorites")} data-testid="tab-favorites"
          className={`text-xs px-3 py-1.5 rounded transition ${tab === "favorites" ? "bg-brand-amber text-bg font-bold" : "text-ink-muted hover:text-ink-primary"}`}>
          <Heart className="h-3.5 w-3.5 inline mr-1" /> Favorites
          {status?.favorites_count ? <span className="ml-1 font-mono">({status.favorites_count})</span> : null}
        </button>
      </div>

      {tab === "search" ? (
      <div data-testid="search-panel">
        {/* Filter bar */}
        <div className="mb-4 rounded-xl border border-line bg-bg-surface p-4">
          <div className="flex flex-col md:flex-row gap-3">
            <div className="relative flex-1">
              <Search className="h-4 w-4 text-ink-muted absolute left-2 top-1/2 -translate-y-1/2" />
              <input
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && search()}
                placeholder="e.g. 'sunrise cinematic build', 'lofi morning show bed'"
                data-testid="search-input"
                className="w-full pl-8 pr-3 py-2 rounded-md border border-line bg-bg-elevated text-ink-primary text-sm focus:border-brand-amber focus:outline-none"
              />
            </div>
            <button
              onClick={search}
              disabled={!connected || searching}
              data-testid="search-btn"
              className="px-4 py-2 rounded-md bg-brand-amber text-bg font-bold text-sm disabled:opacity-50 inline-flex items-center justify-center gap-2"
            >
              {searching ? <Loader2 className="h-4 w-4 animate-spin" /> : <Search className="h-4 w-4" />} Search
            </button>
          </div>
          <div className="mt-3 grid grid-cols-2 md:grid-cols-6 gap-2">
            <select value={genre} onChange={(e) => setGenre(e.target.value)} data-testid="filter-genre"
              className="text-xs px-2 py-1.5 rounded border border-line bg-bg-elevated text-ink-primary">
              {GENRES.map((g) => <option key={g || "any"} value={g}>{g || "Any genre"}</option>)}
            </select>
            <select value={mood} onChange={(e) => setMood(e.target.value)} data-testid="filter-mood"
              className="text-xs px-2 py-1.5 rounded border border-line bg-bg-elevated text-ink-primary">
              {MOODS.map((m) => <option key={m || "any"} value={m}>{m || "Any mood"}</option>)}
            </select>
            <select value={energy} onChange={(e) => setEnergy(e.target.value)} data-testid="filter-energy"
              className="text-xs px-2 py-1.5 rounded border border-line bg-bg-elevated text-ink-primary">
              {ENERGIES.map((m) => <option key={m || "any"} value={m}>{m ? m.charAt(0).toUpperCase() + m.slice(1) + " energy" : "Any energy"}</option>)}
            </select>
            <input type="number" min="40" max="240" value={bpmMin} onChange={(e) => setBpmMin(e.target.value)}
              placeholder="BPM min" data-testid="filter-bpm-min"
              className="text-xs px-2 py-1.5 rounded border border-line bg-bg-elevated text-ink-primary" />
            <input type="number" min="40" max="240" value={bpmMax} onChange={(e) => setBpmMax(e.target.value)}
              placeholder="BPM max" data-testid="filter-bpm-max"
              className="text-xs px-2 py-1.5 rounded border border-line bg-bg-elevated text-ink-primary" />
            <select value={hasVocals} onChange={(e) => setHasVocals(e.target.value)} data-testid="filter-vocals"
              className="text-xs px-2 py-1.5 rounded border border-line bg-bg-elevated text-ink-primary">
              <option value="">Vocals: any</option>
              <option value="no">Instrumental only</option>
              <option value="yes">With vocals</option>
            </select>
          </div>
        </div>

        {/* Results */}
        {searching ? (
          <div className="py-10 text-center font-mono text-xs text-ink-muted" data-testid="search-loading">
            <Loader2 className="h-6 w-6 animate-spin text-brand-amber mx-auto mb-2" /> Searching catalog…
          </div>
        ) : results.items.length === 0 ? (
          <div className="rounded-xl border border-dashed border-line bg-bg-surface p-10 text-center" data-testid="search-empty">
            <Music className="h-10 w-10 text-ink-muted mx-auto mb-2" />
            <div className="text-sm text-ink-muted">
              {connected ? "Type a query above and hit Search." : "Connect Epidemic Sound to start searching."}
            </div>
          </div>
        ) : (
          <div className="space-y-1.5" data-testid="search-results">
            {results.items.map((t) => (
              <TrackRow
                key={t.track_id} track={t}
                isPlaying={playingId === t.track_id}
                isFavorite={favIds.has(t.track_id)}
                onPlay={playPreview}
                onToggleFav={toggleFav}
                onAttach={openAttach}
                onDropImaging={dropToImaging}
                isDropping={droppingId === t.track_id}
              />
            ))}
            <div className="mt-3 text-[11px] font-mono text-ink-muted">{results.total} result(s)</div>
          </div>
        )}
      </div>
      ) : (
      <div data-testid="favorites-panel">
        {favorites.items.length === 0 ? (
          <div className="rounded-xl border border-dashed border-line bg-bg-surface p-10 text-center" data-testid="favorites-empty">
            <Heart className="h-10 w-10 text-ink-muted mx-auto mb-2" />
            <div className="text-sm text-ink-muted">No favorites yet — heart a track from Search.</div>
          </div>
        ) : (
          <div className="space-y-1.5">
            {favorites.items.map((t) => (
              <TrackRow
                key={t.track_id} track={t}
                isPlaying={playingId === t.track_id}
                isFavorite={true}
                onPlay={playPreview}
                onToggleFav={toggleFav}
                onAttach={openAttach}
                onDropImaging={dropToImaging}
                isDropping={droppingId === t.track_id}
              />
            ))}
          </div>
        )}
      </div>
      )}

      {/* Connect modal */}
      {showConnect && (
        <div className="fixed inset-0 z-50 grid place-items-center bg-bg/80 backdrop-blur p-4" onClick={() => setShowConnect(false)} data-testid="connect-modal">
          <div className="w-full max-w-md rounded-xl border border-line bg-bg-surface p-6" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-start gap-3 mb-4">
              <div className="rounded-md p-2 bg-brand-amber/10 text-brand-amber"><Plug className="h-5 w-5" /></div>
              <div className="flex-1 min-w-0">
                <h3 className="font-display text-lg font-bold">Connect Epidemic Sound</h3>
                <p className="text-xs text-ink-muted mt-0.5">Paste your Bearer JWT or <code className="font-mono">accessKeyId:accessKeySecret</code>. Stored encrypted.</p>
              </div>
              <button onClick={() => setShowConnect(false)} className="text-ink-muted hover:text-ink-primary" data-testid="connect-close">
                <X className="h-4 w-4" />
              </button>
            </div>
            <label className="text-[11px] font-mono uppercase tracking-wider text-ink-muted">Credential</label>
            <div className="mt-1 relative">
              <textarea
                value={credential}
                onChange={(e) => setCredential(e.target.value)}
                rows={4}
                placeholder="eyJhbGciOi..."
                data-testid="connect-input"
                className={`w-full rounded-md border border-line bg-bg-elevated text-ink-primary px-3 py-2 text-xs font-mono focus:border-brand-amber focus:outline-none ${showCred ? "" : "text-transparent caret-ink-primary selection:bg-brand-amber/30"}`}
                style={showCred ? {} : { WebkitTextSecurity: "disc" }}
              />
              <button type="button" onClick={() => setShowCred(!showCred)} className="absolute right-2 top-2 text-ink-muted" data-testid="connect-toggle-show">
                {showCred ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
              </button>
            </div>
            <button
              onClick={connect}
              disabled={connecting || credential.trim().length < 20}
              data-testid="connect-submit"
              className="mt-4 w-full px-4 py-2.5 rounded-md bg-brand-amber text-bg font-bold inline-flex items-center justify-center gap-2 disabled:opacity-50"
            >
              {connecting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plug className="h-4 w-4" />}
              {connecting ? "Probing credential…" : "Connect"}
            </button>
            <div className="mt-2 text-[10px] text-ink-muted text-center">We probe <code>/v1/me</code> before persisting — bad creds never touch storage.</div>
          </div>
        </div>
      )}

      {/* Attach to Vision project modal */}
      {attachTrack && (
        <div className="fixed inset-0 z-50 grid place-items-center bg-bg/80 backdrop-blur p-4" onClick={() => setAttachTrack(null)} data-testid="attach-modal">
          <div className="w-full max-w-md rounded-xl border border-line bg-bg-surface p-6" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-start gap-3 mb-4">
              <div className="rounded-md p-2 bg-brand-blue/10 text-brand-blue"><Film className="h-5 w-5" /></div>
              <div className="flex-1 min-w-0">
                <h3 className="font-display text-lg font-bold">Attach as Music Bed</h3>
                <p className="text-xs text-ink-muted mt-0.5 truncate">
                  <strong className="text-ink-primary">{attachTrack.title || "Untitled"}</strong>
                  {attachTrack.artist ? ` · ${attachTrack.artist}` : ""}
                </p>
              </div>
              <button onClick={() => setAttachTrack(null)} className="text-ink-muted hover:text-ink-primary" data-testid="attach-close">
                <X className="h-4 w-4" />
              </button>
            </div>
            <div className="text-[11px] font-mono uppercase tracking-wider text-ink-muted mb-2">Choose a Vision project</div>
            {visionLoading ? (
              <div className="py-8 text-center text-xs text-ink-muted">
                <Loader2 className="h-5 w-5 animate-spin mx-auto mb-2 text-brand-amber" /> Loading projects…
              </div>
            ) : visionProjects.length === 0 ? (
              <div className="rounded-md border border-dashed border-line bg-bg-elevated p-6 text-center" data-testid="attach-empty">
                <Film className="h-8 w-8 text-ink-muted mx-auto mb-2" />
                <div className="text-sm text-ink-muted mb-3">No Vision projects yet.</div>
                <Link to="/dashboard/vision-studio" className="inline-block rounded-md bg-brand-amber text-bg font-bold px-3 py-1.5 text-xs">
                  Open Vision Studio →
                </Link>
              </div>
            ) : (
              <div className="max-h-72 overflow-y-auto space-y-1.5" data-testid="attach-project-list">
                {visionProjects.map((p) => {
                  const already = (p.music_bed?.track_id || "") === attachTrack.track_id;
                  return (
                    <button
                      key={p.project_id}
                      onClick={() => doAttach(p)}
                      disabled={attaching === p.project_id || already}
                      data-testid={`attach-to-${p.project_id}`}
                      className="w-full flex items-center gap-3 rounded-md border border-line bg-bg-elevated p-3 text-left hover:border-brand-blue/40 disabled:opacity-60 transition"
                    >
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-medium text-ink-primary truncate">{p.title || "Untitled"}</div>
                        <div className="text-[11px] text-ink-muted truncate">
                          {p.project_type} · {p.aspect} · {p.duration_seconds}s
                          {p.music_bed?.track_id ? ` · current bed: ${p.music_bed.title || p.music_bed.track_id}` : ""}
                        </div>
                      </div>
                      {attaching === p.project_id ? (
                        <Loader2 className="h-4 w-4 animate-spin text-brand-blue" />
                      ) : already ? (
                        <span className="text-[11px] font-mono text-emerald-300 inline-flex items-center gap-1"><Check className="h-3.5 w-3.5" /> Attached</span>
                      ) : (
                        <Plus className="h-4 w-4 text-brand-blue" />
                      )}
                    </button>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Sticky mini player — keeps the preview alive while browsing */}
      {npTrack && (
        <div
          data-testid="preview-player-bar"
          className="fixed bottom-4 left-1/2 -translate-x-1/2 z-40 w-[min(760px,92vw)] rounded-xl border border-line bg-bg-surface/90 backdrop-blur-xl shadow-2xl px-4 py-3 flex items-center gap-3"
        >
          {npTrack.cover_url ? (
            <img src={npTrack.cover_url} alt="" className="h-10 w-10 rounded-md object-cover shrink-0" />
          ) : (
            <div className="h-10 w-10 rounded-md bg-bg-elevated grid place-items-center shrink-0">
              <Music className="h-4 w-4 text-ink-muted" />
            </div>
          )}
          <div className="min-w-0 w-36 sm:w-44 shrink-0">
            <div className="text-sm text-ink-primary truncate" data-testid="player-bar-title">{npTrack.title}</div>
            <div className="text-[11px] text-ink-muted truncate">{npTrack.artist}</div>
          </div>
          <button
            onClick={() => (playingId ? pausePlayback() : resumePlayback())}
            data-testid="player-bar-toggle"
            className="h-9 w-9 rounded-full bg-brand-amber text-black grid place-items-center shrink-0 hover:brightness-110 transition"
          >
            {playingId ? <Pause className="h-4 w-4" /> : <Play className="h-4 w-4 ml-0.5" />}
          </button>
          <span className="font-mono text-[10px] text-ink-muted w-9 text-right shrink-0">{fmtTime(curTime)}</span>
          <input
            type="range"
            min={0}
            max={duration || 0}
            step={0.1}
            value={Math.min(curTime, duration || 0)}
            onChange={(e) => seekTo(e.target.value)}
            data-testid="player-bar-seek"
            className="flex-1 h-1 accent-amber-400 cursor-pointer"
          />
          <span className="font-mono text-[10px] text-ink-muted w-9 shrink-0">{fmtTime(duration)}</span>
          <Volume2 className="h-4 w-4 text-ink-muted shrink-0 hidden sm:block" />
          <input
            type="range"
            min={0}
            max={1}
            step={0.05}
            value={volume}
            onChange={(e) => changeVolume(e.target.value)}
            data-testid="player-bar-volume"
            className="w-16 sm:w-20 h-1 accent-amber-400 cursor-pointer hidden sm:block"
          />
          <button onClick={closePlayer} data-testid="player-bar-close" className="text-ink-muted hover:text-ink-primary shrink-0" title="Close player">
            <X className="h-4 w-4" />
          </button>
        </div>
      )}
    </div>
  );
}
